<template lang="pug">
    div
        Row.selectorRow(
            v-for="key in selector"
            :key="key"
        )
            Col(span="6")
                Strong {{ $t('global.select') }} {{ $t(selectorLabels(key)) }}
            Col(span="18")
                FormSelect(
                    v-model="formData[key]"
                    :formData="formData"
                    :formItem="selectorOptions(key)"
                    @input="onSelectorChanged(key)"
                    style="width: 200px;"
                )
        Divider
        virtual-table(
                :data="tableData"
                :columns="tableColumns"
                :minWidth="600"
                :loading="loading"
                ref="edgeSelectorTable"
                @on-selection-change="handleSelectRow"
                )
        Page(
            :total="tableDataTotal"
            :page-size="pageSize"
            :current="pageCurrent"
            @on-change="changePage"
            show-total
            style="margin-top: 8px;"
        )
            span Select {{ selectedItems.length }} of {{ tableDataTotal }} items
        Divider
</template>

<script>
import FormSelect from "@/components/rcmTableFormItems/Select/index.js";
import VirtualTable from "@/components/VirtualTable";
import { getFormOptions } from "@/api/formoptions";
import { getEsData } from "@/api/esdata";

export default {
    name: "EdgeSelector",
    components: { FormSelect, VirtualTable },
    props: {
        selector: {
            default: () => ["groupName", "serviceProvideType"],
        },
        givenSearchFilters: { default: () => [] },
        esIndex: { type: String, default: "" },
        action: { type: String, default: "" },
    },
    data() {
        return {
            formData: {
                groupName: "",
                serviceProvideType: "",
            },
            selectedItems: [],
            selectedGroupName: "",
            groupNameSelectorOptions: {
                placeholder: this.$i18n.t("domainsetting.groupName"),
                options: [],
                remoteOptions: async (queryValue, queryParams) => {
                    queryValue = queryValue || "*";
                    queryParams.groupNames = `*${queryValue}*`;
                    let queryString = new URLSearchParams(queryParams).toString();
                    let result = await getFormOptions(`groupnames?${queryString}`);
                    return result.data.options;
                },
                queryParamsFields: [],
                defaultVal: "",
                disabled: this.$store.state.queryGroupName !== "all",
            },
            serviceProvideTypeSelectorOptions: {
                placeholder: this.$i18n.t("edgesetting.serviceProvideType"),
                options:
                    this.$store.state.userData.privilege !== "admin"
                        ? [{ label: "Normal", value: "normal" }]
                        : [
                              { label: "Normal", value: "normal" },
                              { label: "Protect", value: "protect" },
                          ],
                defaultVal: this.$store.state.userData.privilege === "admin" ? "" : "normal",
                disabled: () => this.formData.groupName == "all" || this.$store.state.userData.privilege !== "admin",
            },
            tableColumns: [
                {
                    type: "selection",
                    width: 60,
                    align: "center",
                },
                {
                    title: this.$i18n.t("edgesetting.hostname"),
                    key: "nodeId",
                },
                {
                    title: this.$i18n.t("edgesetting.serviceIP"),
                    key: "serviceIP",
                },
                {
                    title: this.$i18n.t("edgesetting.serviceProvideType"),
                    key: "serviceProvideType",
                },
            ],
            tableData: [],
            tableDataTotal: 0,
            loading: false,
            pageSize: 300,
            pageCurrent: 1,
        };
    },
    methods: {
        selectorLabels(key) {
            return `edgesetting.${key}`;
        },
        selectorOptions(key) {
            const handler = {
                groupName: this.groupNameSelectorOptions,
                serviceProvideType: this.serviceProvideTypeSelectorOptions,
            };
            return handler[key];
        },
        getItems() {
            this.selectedItems = [];
            if (!this.formData.groupName || this.formData.groupName === "all") {
                this.tableData = [];
                this.tableDataTotal = 0;
                this.loading = false;
                return;
            }

            this.loading = true;
            getEsData(this.searchOptions)
                .then((res) => {
                    this.tableData = res.data.mapData;
                    this.tableDataTotal = res.data.total;
                    this.loading = false;
                })
                .catch((err) => {
                    console.log(err);
                    this.loading = false;
                });
        },
        onSelectedGroupNameChanged() {
            // 清空選取
            this.formData.serviceProvideType = this.defaultServiceProvideType;
            this.selectedItems = [];
            this.getItems();
        },
        onSelectedServiceProvideTypeChanged() {
            this.selectedItems = [];
            this.getItems();
        },
        onSelectorChanged(key) {
            const handlers = {
                groupName: this.onSelectedGroupNameChanged,
                serviceProvideType: this.onSelectedServiceProvideTypeChanged,
            };
            return handlers[key]();
        },
        handleSelectRow(selectedItems) {
            this.selectedGroupName = this.getSelectedGroupName(selectedItems);
            this.selectedItems = selectedItems.map((e) => e.nodeId);
        },
        getSelectedGroupName(selectedItems) {
            const isAdminSelectProtectEdge =
                this.$store.state.userData.privilege === "admin" && this.formData.serviceProvideType === "protect";
            if (!isAdminSelectProtectEdge) return this.formData.groupName;
            const groupNameIntersection = selectedItems.reduce(
                (groupName, edge) => {
                    const edgeGroupNames = new Set(edge.groupName || []);
                    return groupName.filter((name) => edgeGroupNames.has(name));
                },
                [...new Set(selectedItems.flatMap((edge) => edge.groupName || []))]
            );
            return groupNameIntersection.join(",");
        },
        changePage(page) {
            this.pageCurrent = page;
            this.getItems();
        },
    },
    computed: {
        defaultServiceProvideType() {
            return this.$store.state.userData.privilege === "admin" ? "" : "normal";
        },
        searchFilters() {
            const filterConfigs = [
                { key: "groupName", type: "string" },
                { key: "serviceProvideType", type: "string" },
            ];
            return filterConfigs
                .filter(({ key }) => Boolean(this.formData[key]) && this.formData[key].length > 0)
                .map(({ key, type }) => ({
                    key,
                    type,
                    value: this.formData[key],
                }));
        },
        searchOptions() {
            const options = {
                isFuzzySearch: false,
                esIndex: this.esIndex,
                from: this.pageSize * (this.pageCurrent - 1),
                size: this.pageSize,
                sort: [{ "nodeId.keyword": { order: "asc" } }],
                filters: this.searchFilters,
                searchView: "EdgeSetting",
                action: this.action,
            };
            return options;
        },
    },
    watch: {
        selectedItems: function (newVal, oldVal) {
            this.$emit("input", {
                selectedGroupName: this.selectedGroupName,
                selectedItems: newVal,
            });
        },
    },
    mounted() {
        this.$emit("input", {
            selectedGroupName: "",
            selectedItems: [],
        });
        this.givenSearchFilters.forEach((filter) => {
            this.formData[filter.key] = filter.value;
        });
        if (this.formData.groupName && this.formData.groupName !== "all") {
            this.formData.serviceProvideType = this.defaultServiceProvideType;
            this.getItems();
        }
    },
    beforeDestroy() {
        this.$emit("input", {
            selectedGroupName: "",
            selectedItems: [],
        });
    },
};
</script>
<style scoped>
.selectorRow {
    margin: 10px 5px 10px 5px;
}
</style>



// WEBPACK FOOTER //
// src/components/EdgeSelector.vue